/**
 * Spoken alerts.
 *
 * This is the single most fragile part of the product, and the reason "Nis udhetimin"
 * is a mandatory gesture. Rules that drove this design (plan section 8):
 *
 *  - Android Chrome exempts INSTALLED PWAs from autoplay restrictions entirely.
 *  - iOS Safari has no such exemption. The gesture requirement is lifted per media
 *    ELEMENT on its first gesture-initiated play(). An element created later is still
 *    blocked. So we create exactly one <audio> element and one AudioContext, both
 *    inside the gesture, and never create another.
 *  - The gesture chain is synchronous: an await before play() breaks it.
 *  - iOS drops the AudioContext into "interrupted" on backgrounding and does not
 *    reliably auto-resume, so every visibilitychange must try to resume it.
 *  - Web Audio is silenced by the iOS hardware mute switch; <audio> elements are not.
 *    That is why the element is kept as a fallback rather than dropped.
 *
 * There is no Albanian speechSynthesis voice on either platform, so every line is a
 * pre-recorded static file. No runtime TTS, no network dependency mid-drive.
 */

const SILENT_WAV =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

export type AudioStatus = "locked" | "ready" | "interrupted";

export class VoiceAlerts {
  private el: HTMLAudioElement | null = null;
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private volume = 0.9;
  private buffers = new Map<string, AudioBuffer>();
  private unlocked = false;
  private lastSpokenAt = 0;

  get status(): AudioStatus {
    if (!this.unlocked) return "locked";
    if (this.ctx !== null && this.ctx.state === "suspended") return "interrupted";
    return "ready";
  }

  /**
   * MUST be called synchronously from a real user gesture (the "Nis udhetimin" tap).
   * Everything that needs the gesture happens before the first await.
   */
  unlockFromGesture(): void {
    if (this.unlocked) return;

    // 1. One long-lived element. We only ever swap its src afterwards.
    const el = new Audio();
    el.preload = "auto";
    el.src = SILENT_WAV;
    void el.play().catch(() => {
      /* Element stays usable for later src swaps even if this first play is refused. */
    });
    this.el = el;

    // 2. One AudioContext, resumed inside the same gesture.
    type Ctor = typeof AudioContext;
    const Ctx: Ctor | undefined =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
    if (Ctx !== undefined) {
      const ctx = new Ctx({ latencyHint: "interactive" });
      void ctx.resume().catch(() => undefined);
      const gain = ctx.createGain();
      gain.gain.value = this.volume;
      gain.connect(ctx.destination);
      this.ctx = ctx;
      this.gain = gain;
    }

    // 3. Ask iOS to duck the driver's music rather than fight it. "transient-solo"
    //    is specced for exactly this case: pause other audio, then restore it.
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    if (nav.audioSession !== undefined) {
      try { nav.audioSession.type = "transient-solo"; } catch { /* older WebKit */ }
    }

    this.unlocked = true;
  }

  /** Pre-decode every clip once, so playback is a synchronous buffer start. */
  async preload(urls: readonly string[]): Promise<void> {
    const ctx = this.ctx;
    if (ctx === null) return;
    await Promise.all(
      urls.map(async (url) => {
        if (this.buffers.has(url)) return;
        try {
          const res = await fetch(url);
          if (!res.ok) return;
          this.buffers.set(url, await ctx.decodeAudioData(await res.arrayBuffer()));
        } catch {
          /* A missing clip must never break the drive; we fall back to the element. */
        }
      }),
    );
  }

  /** Call on every visibilitychange -> visible. Assume each app switch killed audio. */
  async resume(): Promise<AudioStatus> {
    if (this.ctx !== null && this.ctx.state !== "running") {
      try { await this.ctx.resume(); } catch { /* still interrupted */ }
    }
    return this.status;
  }

  /**
   * Speak one clip. Buffer path first (reliable in-session), element path as the
   * fallback that also survives the iOS mute switch.
   * `minGapMs` stops two hazards talking over each other.
   */
  speak(url: string, minGapMs = 2500): boolean {
    const now = Date.now();
    if (now - this.lastSpokenAt < minGapMs) return false;
    this.lastSpokenAt = now;

    const ctx = this.ctx;
    const buf = this.buffers.get(url);
    if (ctx !== null && ctx.state === "running" && buf !== undefined) {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gain ?? ctx.destination);
      src.start();
      return true;
    }

    const el = this.el;
    if (el !== null) {
      el.src = url;
      el.volume = this.volume;
      void el.play().catch(() => undefined);
      return true;
    }
    return false;
  }

  /**
   * Synthesised earcons — no files, no language. "far": one soft tone, the
   * manoeuvre is coming; "near": two quick tones, do it now; "hazard": a lower
   * double tone, the fallback when a voice clip is missing so a hazard is never
   * silent. Web Audio only: an earcon is not worth the element's mute-switch bypass.
   */
  chime(kind: "far" | "near" | "hazard"): boolean {
    const ctx = this.ctx;
    if (ctx === null || ctx.state !== "running") return false;
    const out = this.gain ?? ctx.destination;
    const notes: [number, number, number][] = kind === "far"
      ? [[880, 0, 0.18]]
      : kind === "near" ? [[880, 0, 0.12], [1175, 0.16, 0.14]]
      : [[440, 0, 0.16], [330, 0.2, 0.22]];
    for (const [freq, at, dur] of notes) {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      env.gain.setValueAtTime(0.0001, ctx.currentTime + at);
      env.gain.exponentialRampToValueAtTime(0.6, ctx.currentTime + at + 0.015);
      env.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + dur);
      osc.connect(env);
      env.connect(out);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + dur + 0.02);
    }
    return true;
  }

  /**
   * Several clips back to back ("Pas treqind metrash," + "kthehu majtas."),
   * buffer path only, each starting when the previous ends. Returns false when
   * any clip is missing, so the caller can fall back to an earcon.
   */
  speakSequence(urls: readonly string[], minGapMs = 1500): boolean {
    const ctx = this.ctx;
    if (ctx === null || ctx.state !== "running") return false;
    if (!urls.every((u) => this.buffers.has(u))) return false;
    const now = Date.now();
    if (now - this.lastSpokenAt < minGapMs) return false;
    this.lastSpokenAt = now;
    let at = ctx.currentTime;
    for (const u of urls) {
      const buf = this.buffers.get(u) as AudioBuffer;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.gain ?? ctx.destination);
      src.start(at);
      at += buf.duration + 0.12;
    }
    return true;
  }

  /** Whether a clip is decoded and ready — false means speak() would fall back. */
  hasClip(url: string): boolean {
    return this.buffers.has(url);
  }

  /** 0..1. Applied to the Web Audio gain and the fallback element alike. */
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.gain !== null) this.gain.gain.value = this.volume;
    if (this.el !== null) this.el.volume = this.volume;
  }
}
